/** @jsx jsx */

import { ChangeEvent, Fragment } from 'react';

import { css, jsx } from '@emotion/core';
import {
  Dropdown,
  Field,
  LabeledCheckbox,
  LabeledRadio,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';

import EditorBodyLayout from 'app/developer-v3/layouts/EditorBodyLayout';
import Link from 'app/common/components/Link';
import StaticDropdownInputFieldZinnia from 'app/developer-v3/components/StaticDropdownInputFieldZinnia';
import { getAppVersionUrl } from 'app/developer-v3/utils';
import { BASE_DOC_URL_VB, NEW_SERVICE_FIELD } from 'app/developer-v3/constants';
import {
  useCurrentVersionReadOnlyReason,
  useForm,
} from 'app/developer-v3/hooks';

import type { AuthFieldParams } from 'app/developer-v3/types';
import type { AuthFieldSchema } from 'app/developer-v3/platformSchema/field';

import { FIELD_VERTICAL_SPACING, TypeItems } from './constants';

const Styles = {
  booleanInput: css`
    display: flex;
    gap: 28px;
  `,
};

type Props = {
  initialAuthField: AuthFieldSchema;
  isSaving: boolean;
  params: AuthFieldParams;
  upsertAuthField: (authField: AuthFieldSchema, key?: string) => Promise<void>;
};

const defaultProps = {
  initialAuthField: {
    key: '',
    required: false,
  },
};

function AuthenticationFieldEditor({
  initialAuthField,
  isSaving,
  params,
  upsertAuthField,
}: Props) {
  const form = useForm<AuthFieldSchema>({
    enableReinitialize: true,
    initialValues: {
      choices: initialAuthField?.choices ?? undefined,
      default: initialAuthField?.default ?? undefined,
      helpText: initialAuthField?.helpText ?? undefined,
      inputFormat: initialAuthField?.inputFormat ?? undefined,
      // An access token (key: 'access_token') or refresh token
      // (key: 'refresh_token') field falls back to be a computed field if the
      // computed property is missing.
      computed:
        initialAuthField.computed ??
        ['access_token', 'refresh_token'].includes(initialAuthField.key),
      ...initialAuthField,
      key: initialAuthField?.key ?? '',
      label: initialAuthField?.label ?? undefined,
      required: initialAuthField?.required ?? undefined,
      type: initialAuthField?.type ?? undefined,
    },
    async onSubmit(valuesNext: AuthFieldSchema) {
      return upsertAuthField(
        valuesNext,
        params.fieldKey === NEW_SERVICE_FIELD ? undefined : params.fieldKey
      );
    },
    validate: valuesNext => {
      const { key } = valuesNext;
      const errorsNext: Partial<Record<keyof AuthFieldSchema, string>> = {};

      if (key.trim().length === 0) {
        errorsNext.key = 'Key is required.';
      }

      return errorsNext;
    },
  });

  // TODO: Use form.isSubmitting as the only flag to determine if the fom is
  // being submitted, without needing isSaving.
  // Ideally form.isSubmitting is true whenever isSaving is true. We'd depend on
  // form.isSubmitting only. In other words, form.isSubmitting becomes true when
  // onSubmit starts running, and false when finishes. But today the callback
  // upsertAuthField called in onSubmit updates the auth field in the Redux
  // store via Entity and changes initialAuthField right away. That makes this
  // AuthenticationFieldEditor component rerender soon and loses the form state.
  // Therefore we have to listen to isSaving additionally for the time being.
  const isDisabled =
    !!useCurrentVersionReadOnlyReason() || isSaving || form.isSubmitting;

  return (
    <EditorBodyLayout
      cancelRoute={`${getAppVersionUrl(
        params.id,
        params.version
      )}/authentication/edit`}
      isDisabled={isDisabled || !form.isValid || !form.isDirty}
      isSaving={form.isSubmitting}
      onSave={form.onSubmit}
      saveText={initialAuthField.key ? 'Save' : 'Add'}
    >
      <Field
        helpTextLineClamp={2}
        isDisabled={isDisabled}
        isRequired={true}
        label="Field type"
        renderHelpText={() => (
          <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
            Use standard Fields to request info from users. Computed Fields are
            used programmatically and not shown to users.{' '}
            <Link
              href={`${BASE_DOC_URL_VB}/build-cli/input-fields#computed-fields`}
              target="_blank"
            >
              Learn more
            </Link>
            .
          </Text>
        )}
        renderInput={() => (
          <div css={Styles.booleanInput}>
            <LabeledRadio
              checked={!form.values.computed}
              isDisabled={isDisabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                if (event.target.checked) {
                  if (!form.isTouched.type) {
                    form.setFieldTouched('computed', true);
                  }
                  form.setFieldValue('computed', false);
                }
              }}
              tabIndex={0}
              value="regular"
            >
              Field
            </LabeledRadio>
            <LabeledRadio
              checked={form.values.computed}
              isDisabled={isDisabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                if (event.target.checked) {
                  if (!form.isTouched.type) {
                    form.setFieldTouched('computed', true);
                  }
                  form.setFieldValue('computed', true);
                }
              }}
              tabIndex={0}
              value="computed"
            >
              Computed Field
            </LabeledRadio>
          </div>
        )}
      />
      {!form.values.computed && (
        <Fragment>
          <Spacer height={FIELD_VERTICAL_SPACING} />
          <Field
            helpTextLineClamp={2}
            isDisabled={isDisabled}
            label="Label"
            renderHelpText={() => (
              <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
                Enter the field’s friendly name for users.
              </Text>
            )}
            renderInput={inputProps => (
              <TextInput
                {...inputProps}
                aria-label="Label"
                name="label"
                onBlur={form.onBlur}
                onChange={form.onChange}
                value={form.values.label}
              />
            )}
          />
        </Fragment>
      )}
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        error={form.isTouched.key ? form.errors.key : ''}
        helpTextLineClamp={2}
        isDisabled={isDisabled}
        isRequired={true}
        label="Key"
        renderHelpText={() => (
          <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
            Key of this field. E.g. "api_key."
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="Key"
            name="key"
            onBlur={form.onBlur}
            onChange={form.onChange}
            value={form.values.key}
          />
        )}
      />
      {!form.values.computed && (
        <Fragment>
          <Spacer height={FIELD_VERTICAL_SPACING} />
          <Field
            error={form.isTouched.required ? form.errors.required : ''}
            helpTextLineClamp={2}
            isDisabled={isDisabled}
            label="Required"
            renderHelpText={() => (
              <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
                Does this field require a value? Check if yes.
              </Text>
            )}
            renderInput={() => (
              <div css={Styles.booleanInput}>
                <LabeledCheckbox
                  aria-label="Required"
                  checked={form.values.required}
                  disabled={isDisabled}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const isChecked = event.target.checked;
                    form.setFieldTouched('required', isChecked);
                    form.setFieldValue('required', isChecked);
                  }}
                  tabIndex={0}
                >
                  Yes
                </LabeledCheckbox>
              </div>
            )}
          />
        </Fragment>
      )}
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        helpTextLineClamp={2}
        isDisabled={isDisabled}
        label="Type"
        renderHelpText={() => (
          <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
            Select the field type. Use String (default) for most text input, or
            Password to obscure text for secret values.
          </Text>
        )}
        renderInput={inputProps => (
          <Dropdown
            {...inputProps}
            aria-label="Type"
            initialSelectedItem={TypeItems.find(
              item => item.value === form.values.type
            )}
            items={TypeItems}
            onBlur={() => form.setFieldTouched('type', true)}
            onChange={item => form.setFieldValue('type', item.value)}
          />
        )}
      />
      {!form.values.computed && (
        <Fragment>
          <Spacer height={FIELD_VERTICAL_SPACING} />
          <Field
            helpTextLineClamp={2}
            isDisabled={isDisabled}
            label="Help text"
            renderHelpText={() => (
              <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
                Explain to users what to include in this field, especially for
                API keys and other hard to find info. Include directions to find
                the data and links to your app settings or help docs.{' '}
                <Link
                  href={`${BASE_DOC_URL_VB}/build-cli/input-fields#computed-fields`}
                  target="_blank"
                >
                  Learn more
                </Link>
                .
              </Text>
            )}
            renderInput={inputProps => (
              <TextInput
                {...inputProps}
                aria-label="Help text"
                name="helpText"
                onBlur={form.onBlur}
                onChange={form.onChange}
                value={form.values.helpText}
              />
            )}
          />
        </Fragment>
      )}
      {!form.values.computed && (
        <Fragment>
          <Spacer height={FIELD_VERTICAL_SPACING} />
          <Field
            helpTextLineClamp={2}
            isDisabled={isDisabled}
            label="Input format"
            renderHelpText={() => (
              <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
                Help users figure out exactly what piece of data you need them
                to enter by providing a string mask for the input field.
                Commonly used when prompting for a part of a URL. E.g.
                "https://&#123;&#123;input&#125;&#125; .yourdomain.com/" or
                "&#123;&#123;input&#125;&#125;@yourdomain.com"
              </Text>
            )}
            renderInput={inputProps => (
              <TextInput
                {...inputProps}
                aria-label="Input format"
                name="inputFormat"
                onBlur={form.onBlur}
                onChange={form.onChange}
                value={form.values.inputFormat}
              />
            )}
          />
        </Fragment>
      )}
      <Spacer height={FIELD_VERTICAL_SPACING} />
      <Field
        helpTextLineClamp={2}
        isDisabled={isDisabled}
        label="Default value"
        renderHelpText={() => (
          <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
            Include a default value for this field as a fallback. For optional
            fields, the default value is set on initial creation and used
            instead of missing or null values every time the Zap runs. For
            required fields, this value is used during Zap creation, but not
            when the Zap runs (Zapier raises an error for missing/null values
            instead).
          </Text>
        )}
        renderInput={inputProps => (
          <TextInput
            {...inputProps}
            aria-label="Default value"
            name="default"
            onBlur={form.onBlur}
            onChange={form.onChange}
            value={form.values.default}
          />
        )}
      />
      {!form.values.computed && (
        <Fragment>
          <Spacer height={FIELD_VERTICAL_SPACING} />
          <StaticDropdownInputFieldZinnia
            initValue={form.values.choices}
            isDisabled={isDisabled}
            isRequired={false}
            onChange={choices => form.setFieldValue('choices', choices)}
            title="Static Dropdown"
          />
        </Fragment>
      )}
    </EditorBodyLayout>
  );
}

AuthenticationFieldEditor.defaultProps = defaultProps;

export default AuthenticationFieldEditor;
